/**
 * Bringing a journal in from somewhere else.
 *
 * `docs/17` finding 1 is "Nobody switched", and the reason it gives is that
 * the person most likely to pay already journals — and therefore already has
 * history they would have to abandon. This screen is the answer to that, so it
 * is written for somebody who has never used AEGIS and is deciding whether to.
 *
 * ## Three steps, and the middle one is the product
 *
 * Choose a file, confirm what its columns mean, import. The confirmation is
 * not a formality: automatic mapping is how a "Fee" column silently lands in
 * `commission` and every net P&L is wrong by an amount nobody can find. AEGIS
 * suggests; the reader agrees.
 *
 * ## Why the result names a disagreement
 *
 * A file's own P&L total and the one AEGIS computes from the same prices will
 * sometimes differ — commission handling differs between tools, and a wrong
 * tick value in either product shows up here first. Saying so plainly is the
 * difference between a reader trusting the journal and quietly deciding it is
 * broken.
 */
import { useState } from 'react';
import type { JSX } from 'react';
import { fmtUsd } from '@aegis/api-client';
import type { Account, ImportPreview, ImportResultDto } from '@aegis/api-client';
import { api } from '../lib/api.js';

/** Fields the user can map, per shape, with the label each one is shown under. */
const FIELDS: Readonly<Record<'fills' | 'trades', readonly (readonly [string, string, boolean])[]>> = {
  // [field, label, required]
  fills: [
    ['symbol', 'Symbol', true],
    ['side', 'Buy or sell', true],
    ['quantity', 'Quantity', true],
    ['price', 'Fill price', true],
    ['executedAt', 'Time', true],
    ['commission', 'Commission', false],
    ['fees', 'Fees', false],
    ['brokerFillId', 'Fill ID', false],
  ],
  trades: [
    ['symbol', 'Symbol', true],
    ['direction', 'Long or short', true],
    ['quantity', 'Quantity', true],
    ['entryPrice', 'Entry price', true],
    ['openedAt', 'Entry time', true],
    ['exitPrice', 'Exit price', false],
    ['closedAt', 'Exit time', false],
    ['netPnl', 'Net P&L', false],
    ['commission', 'Commission', false],
    ['fees', 'Fees', false],
  ],
};

export function ImportScreen({ accounts }: { accounts: readonly Account[] }): JSX.Element {
  const [shape, setShape] = useState<'fills' | 'trades'>('trades');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [columns, setColumns] = useState<Record<string, string>>({});
  const [dayFirst, setDayFirst] = useState(false);
  /**
   * Chosen, or the first one there is.
   *
   * Derived rather than initialised. `useState(accounts[0]?.id)` snapshots the
   * list on the first render, and on the first render the accounts have not
   * loaded — so it captured '' forever, no account was ever selected, and the
   * Import button stayed disabled with no explanation on screen. Found by
   * clicking through it, not by reading it.
   */
  const [chosen, setChosen] = useState<string | null>(null);
  const accountId = chosen ?? accounts[0]?.id ?? '';
  const [result, setResult] = useState<ImportResultDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function read(file: File): Promise<void> {
    setError(null);
    setResult(null);
    const body = await file.text();
    setText(body);
    setFileName(file.name);
    setBusy(true);
    try {
      const p = await api.importPreview(body, shape);
      setPreview(p);
      setColumns({ ...p.suggested });
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error ? e.message : 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Re-describe the same file under the other shape, without re-reading it.
   *
   * ## Why the `catch` is not optional
   *
   * This used to be `try`/`finally` with no `catch`, while `read()` directly
   * above has all three. The asymmetry mattered: the shape toggle is already
   * flipped by `setShape(next)` on the first line, so a rejected
   * `importPreview` left the *previous* shape's preview on screen underneath
   * the *new* shape's toggle — with nothing said, and `busy` correctly
   * cleared so the screen looked settled.
   *
   * A trader reading "fills" against a preview computed as trades then maps
   * columns for a shape the file was never parsed as. The import that follows
   * is not a crash; it is a wrong journal, which is the failure mode this
   * product can least afford.
   *
   * The unhandled rejection also escaped to `window.onerror`, so the telemetry
   * sink recorded it as a crash with no screen attached.
   */
  async function reshape(next: 'fills' | 'trades'): Promise<void> {
    setShape(next);
    if (text === '') return;
    setBusy(true);
    // Cleared before the attempt, not after: leaving a previous failure on
    // screen during a retry says the new attempt already failed.
    setError(null);
    try {
      const p = await api.importPreview(text, next);
      setPreview(p);
      setColumns({ ...p.suggested });
    } catch (e) {
      // Dropped, not kept. A preview belonging to the other shape is worse
      // than none — it is a claim about a file nobody parsed that way, and
      // the column pickers below read from it.
      setPreview(null);
      setColumns({});
      setError(e instanceof Error ? e.message : 'Could not read that file as ' + next + '.');
    } finally {
      setBusy(false);
    }
  }

  const required = FIELDS[shape].filter(([, , req]) => req).map(([f]) => f);
  const missing = required.filter((f) => (columns[f] ?? '') === '');
  const ready = preview !== null && missing.length === 0 && accountId !== '';

  async function commit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.importJournal({ text, accountId, shape, columns, dayFirst }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="import-screen">
      <div className="section-head"><h2>Import a journal</h2></div>

      <p className="import-lede">
        A CSV from TradeZella, Tradervue, TraderSync, Edgewonk, your broker, or a
        spreadsheet you keep yourself. You tell AEGIS what the columns mean once.
      </p>

      {/* ── step 1 ─────────────────────────────────────────────────────── */}
      <section className="import-step">
        <h3><span className="import-n">1</span>Choose the file</h3>

        <div className="import-shape">
          <button
            type="button"
            className={`chip-btn compact ${shape === 'trades' ? 'on' : ''}`}
            onClick={() => void reshape('trades')}
          >
            Each row is a trade
          </button>
          <button
            type="button"
            className={`chip-btn compact ${shape === 'fills' ? 'on' : ''}`}
            onClick={() => void reshape('fills')}
          >
            Each row is a fill
          </button>
        </div>
        <p className="import-hint">
          Most journal exports are trades. Broker statements are usually fills — and
          fills are better, because AEGIS matches them itself using your own FIFO or
          LIFO setting rather than inheriting someone else's.
        </p>

        <label className="import-file">
          <input
            type="file"
            accept=".csv,.tsv,.txt,text/csv,text/plain"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); }}
          />
          <span>{fileName ?? 'Choose a CSV…'}</span>
        </label>
      </section>

      {/* ── step 2 ─────────────────────────────────────────────────────── */}
      {preview && (
        <section className="import-step">
          <h3><span className="import-n">2</span>Confirm what the columns mean</h3>
          <p className="import-hint">
            {preview.rowCount.toLocaleString()} rows, {preview.headers.length} columns.
            AEGIS has guessed the ones it recognised — check them, especially anything
            about money.
          </p>

          <div className="import-map">
            {FIELDS[shape].map(([field, label, req]) => (
              <label key={field} className="import-field">
                <span className={req ? 'import-req' : undefined}>
                  {label}{req ? ' *' : ''}
                </span>
                <select
                  value={columns[field] ?? ''}
                  onChange={(e) => setColumns((c) => ({ ...c, [field]: e.target.value }))}
                >
                  <option value="">— not in this file —</option>
                  {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>

          <label className="import-dayfirst">
            <input type="checkbox" checked={dayFirst} onChange={(e) => setDayFirst(e.target.checked)} />
            {/* No parser can resolve 03/04/2026 from the value alone, so it is
                asked rather than guessed. A journal silently shifted by nine
                months is a bug the user finds in March. */}
            <span>Dates are day/month/year (03/04 means 3 April, not 4 March)</span>
          </label>

          <div className="import-sample scroller">
            <table>
              <thead>
                <tr>{preview.headers.map((h) => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.sample.map((row, i) => (
                  <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── step 3 ─────────────────────────────────────────────────────── */}
      {preview && (
        <section className="import-step">
          <h3><span className="import-n">3</span>Choose the account and import</h3>

          <div className="account-picker">
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`chip-btn compact ${accountId === a.id ? 'on' : ''}`}
                onClick={() => setChosen(a.id)}
              >
                {a.nickname}
              </button>
            ))}
          </div>

          {missing.length > 0 && (
            <p className="import-missing">
              Still needed: {missing.map((f) => {
                const found = FIELDS[shape].find(([field]) => field === f);
                return found ? found[1] : f;
              }).join(', ')}
            </p>
          )}

          <button type="button" className="btn primary" disabled={!ready || busy} onClick={() => void commit()}>
            {busy ? 'Importing…' : 'Import'}
          </button>
          <p className="import-hint">
            Importing the same file twice adds nothing the second time.
          </p>
        </section>
      )}

      {error && <p className="import-error">{error}</p>}

      {result && (
        <section className="import-result">
          <h3>Imported</h3>
          <dl>
            <div><dt>Rows read</dt><dd>{result.rowsRead.toLocaleString()}</dd></div>
            <div><dt>Added</dt><dd>{result.imported.toLocaleString()}</dd></div>
            {result.duplicates > 0 && (
              <div><dt>Already here</dt><dd>{result.duplicates.toLocaleString()}</dd></div>
            )}
            {result.rejectedCount > 0 && (
              <div><dt>Skipped</dt><dd>{result.rejectedCount.toLocaleString()}</dd></div>
            )}
          </dl>

          {result.statedNetPnl !== null && (
            <p className="import-stated">
              Your file totalled <strong>{fmtUsd(result.statedNetPnl)}</strong>. AEGIS
              recalculates P&amp;L from the prices and each contract's tick value, so the
              journal's own figure may differ — usually because the two tools treat
              commission differently. Both numbers are real; they answer slightly
              different questions.
            </p>
          )}

          {result.rejected.length > 0 && (
            <>
              <h4>Rows AEGIS could not read</h4>
              <p className="import-hint">
                Line numbers match your spreadsheet. Nothing was guessed — a row it
                could not read is a row it left alone.
              </p>
              <ul className="import-rejects">
                {result.rejected.map((r) => (
                  <li key={r.line}><span className="mono">Line {r.line}</span> {r.reason}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}
